<template>
    <UPopover v-model:open="open" :content="{ align: 'end', side: 'bottom', sideOffset: 8 }">
        <!-- The button is the popover's trigger itself, so it carries the expanded state; the
             count sits on the bell inside it. -->
        <UButton color="neutral" variant="ghost" size="sm" square :aria-label="ariaLabel" :title="ariaLabel">
            <UChip :text="badge" :show="notifications.unread > 0" color="error" size="3xl">
                <UIcon name="i-lucide-bell" class="size-4" />
            </UChip>
        </UButton>

        <template #content>
            <div class="flex w-[22rem] max-w-[calc(100vw-2rem)] flex-col">
                <div class="flex items-center justify-between gap-2 border-b border-default px-4 py-3">
                    <p class="text-sm font-semibold text-highlighted">{{ $t('notification.title') }}</p>
                    <UButton
                        v-if="notifications.unread > 0"
                        :label="$t('notification.mark_all_read')"
                        icon="i-lucide-check-check"
                        color="neutral"
                        variant="link"
                        size="xs"
                        @click="markAll"
                    />
                </div>

                <div class="max-h-[min(28rem,70dvh)] overflow-y-auto">
                    <div v-if="notifications.loading && !notifications.loaded" class="flex justify-center py-8">
                        <UIcon name="i-lucide-loader-circle" class="size-5 animate-spin text-muted" />
                    </div>

                    <p v-else-if="notifications.items.length === 0" class="px-4 py-8 text-center text-sm text-muted">
                        {{ $t('notification.empty') }}
                    </p>

                    <ul v-else class="divide-y divide-default">
                        <li v-for="item in notifications.items" :key="item.id">
                            <button
                                type="button"
                                class="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-elevated"
                                @click="select(item)"
                            >
                                <span class="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md" :class="notificationColor(item.kind)">
                                    <UIcon :name="notificationIcon(item.kind)" class="size-4" />
                                </span>

                                <span class="min-w-0 flex-1">
                                    <span class="block text-sm" :class="item.read_at ? 'text-muted' : 'text-highlighted'">
                                        {{ notificationSentence(item.kind) }}
                                        <span v-if="item.task" class="font-medium">{{ item.task.key }} {{ item.task.title }}</span>
                                    </span>
                                    <span class="mt-0.5 block truncate text-xs text-dimmed">
                                        {{ [item.actor?.name, item.project?.name, agoLabel(item.created_at)].filter(Boolean).join(' · ') }}
                                    </span>
                                </span>

                                <span
                                    v-if="!item.read_at"
                                    class="mt-2 size-2 shrink-0 rounded-full bg-primary"
                                    :aria-label="$t('notification.unread')"
                                />
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
        </template>
    </UPopover>
</template>

<script lang="ts" setup>
    import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRouter } from 'vue-router'

    import { safeRedirect } from '@/libs/auth'
    import { agoLabel } from '@/libs/dayjs'
    import { notificationColor, notificationIcon, notificationSentence } from '@/libs/notification'
    import { PrintError } from '@/libs/toast'
    import { useAppStore } from '@/stores/app'
    import { useNotificationsStore } from '@/stores/notifications'
    import { useTaskDrawerStore } from '@/stores/taskDrawer'
    import type { INotification } from '@/types/Notification'

    /*
     * NALOGE: the bell in the topbar's right-hand controls (a recorded deviation from the
     * byte-copied Topbar). The count is polled by `stores/notifications.ts` while this is
     * mounted — which is exactly while somebody is signed in and looking at the shell — and
     * the list is asked for each time the popover opens, so it is never older than a click.
     */
    const { t }         = useI18n()
    const router        = useRouter()
    const appStore      = useAppStore()
    const notifications = useNotificationsStore()
    const taskDrawer    = useTaskDrawerStore()

    const open = ref(false)

    const badge = computed((): string => notifications.unread > 99 ? '99+' : String(notifications.unread))

    const ariaLabel = computed((): string => notifications.unread > 0
        ? t('notification.title_unread', { count: notifications.unread })
        : t('notification.title'))

    watch(open, (value) => {
        if (value) void notifications.loadList()
    })

    /*
     * A notification is about a task, and it says where that task is opened (`path`, docs/API.md
     * → Notifications): its project, with the task's drawer open. Reading it goes there and marks
     * it read. Only an address inside the console is followed; without one the task opens where
     * the reader is.
     */
    const select = async (item: INotification) => {
        open.value = false
        void notifications.markRead(item)

        const target = safeRedirect(item.path)

        if (target) {
            await router.push(target)
        } else if (item.task) {
            taskDrawer.open(item.task.id)
        }
    }

    const markAll = async () => {
        appStore.startLoading()

        const done = await notifications.markAllRead()

        appStore.stopLoading()

        if (!done) PrintError(t('notification.mark_failed'))
    }

    onMounted(() => notifications.start())

    onBeforeUnmount(() => notifications.stop())
</script>
